import React, { useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { SERIE_A_TEAMS, SERIE_B_TEAMS } from '../../constants/data';
import { getTeamCrest } from '../../utils/teamLogos';
import TeamFlag from './TeamFlag';

/* Labels curtos para exibir na grade */
const SHORT_NAME = {
  'Flamengo':      'Fla',
  'Palmeiras':     'Pal',
  'Corinthians':   'Cor',
  'São Paulo':     'SPF',
  'Atlético-MG':   'Galo',
  'Fluminense':    'Flu',
  'Vasco':         'Vas',
  'Internacional': 'Inter',
  'Cruzeiro':      'Cru',
  'Botafogo':      'Bot',
  'Grêmio':        'Grê',
  'Bahia':         'Bah',
  'Athletico-PR':  'CAP',
  'Fortaleza':     'For',
  'Santos':        'San',
  'Cuiabá':        'Cui',
  'América-MG':    'AMG',
  'Coritiba':      'Cori',
  'Ceará':         'Ceá',
  'Sport':         'Spt',
  'Náutico':       'Náu',
  'Goiás':         'Goiás',
  'Chapecoense':   'Chape',
};

/**
 * TeamPicker — seletor visual de time do coração com abas Série A / B.
 *
 * Modo controlado: passar `value` (string) + `onChange(team)`.
 * Modo não-controlado (FormData): passar `name` + `defaultValue`; renderiza
 *   um <input type="hidden"> para ser lido pelo FormData no submit.
 */
const TeamPicker = ({ value, onChange, name, defaultValue }) => {
  const isControlled = value !== undefined;
  const [selected, setSelected] = useState(defaultValue || 'Sem Time');
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('A');

  const current = isControlled ? value : selected;

  const handleSelect = (team) => {
    if (isControlled) {
      onChange(team);
    } else {
      setSelected(team);
    }
    setOpen(false);
  };

  const teams = tab === 'A' ? SERIE_A_TEAMS : SERIE_B_TEAMS;
  const hasCrest = (t) => !!getTeamCrest(t);

  return (
    <div>
      {/* Input oculto para FormData (modo não-controlado) */}
      {!isControlled && <input type="hidden" name={name || 'heartTeam'} value={selected} />}

      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none text-left"
      >
        {current && current !== 'Sem Time' ? (
          <TeamFlag team={current} size="sm" />
        ) : (
          <span className="text-lg">⚽</span>
        )}
        <span className="flex-1 text-sm font-medium text-slate-700">
          {current && current !== 'Sem Time' ? current : 'Sem time'}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Painel expandido */}
      {open && (
        <div className="mt-1.5 bg-white border border-slate-200 rounded-xl shadow-lg p-3">
          {/* Abas */}
          <div className="flex gap-1 mb-3">
            {['A', 'B'].map(s => (
              <button
                key={s}
                type="button"
                onClick={() => setTab(s)}
                className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-colors ${tab === s ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
              >
                Série {s}
              </button>
            ))}
          </div>

          {/* Grade de escudos */}
          <div className="grid grid-cols-4 gap-1.5">
            {teams.map(team => {
              const active = current === team;
              return (
                <button
                  key={team}
                  type="button"
                  onClick={() => handleSelect(team)}
                  className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all ${active ? 'bg-emerald-50 ring-2 ring-emerald-400' : 'hover:bg-slate-50'}`}
                >
                  {hasCrest(team) ? (
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-white shadow-sm flex items-center justify-center border border-slate-100">
                      <img
                        src={getTeamCrest(team)}
                        alt={team}
                        className="w-full h-full object-contain p-0.5"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </div>
                  ) : (
                    <TeamFlag team={team} size="md" />
                  )}
                  <span className={`text-[9px] font-bold text-center leading-tight ${active ? 'text-emerald-700' : 'text-slate-500'}`}>
                    {SHORT_NAME[team] || team}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Sem time */}
          <button
            type="button"
            onClick={() => handleSelect('Sem Time')}
            className={`mt-2 w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-colors ${current === 'Sem Time' ? 'bg-slate-200 text-slate-700' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`}
          >
            <X className="w-3 h-3" /> Sem time
          </button>
        </div>
      )}
    </div>
  );
};

export default TeamPicker;
